import { useQuery } from '@tanstack/react-query';
import { useDataSource } from './useDataSource';
import { ApiError } from '../api/client';
import { useApi, useEngine } from '../api/context';
import { FALLBACK_META, fallbackMeta } from './fallback';
import type { MetaIndex, ResourceMeta } from './types';

// useMeta(resource): GET /v1/meta/resources/:name, cached forever for the session. When the
// backend doesn't serve meta yet (404) — or can't be reached — the derived fallback keeps
// screens rendering. Any other error surfaces normally.

const FOREVER = Number.POSITIVE_INFINITY;

export function useMeta(resource: string | undefined) {
  const api = useApi();
  const { principal } = useEngine();
  return useQuery<ResourceMeta>({
    queryKey: ['meta', resource ?? ''],
    enabled: Boolean(resource),
    staleTime: FOREVER,
    gcTime: FOREVER,
    retry: false,
    queryFn: async () => {
      const name = resource!;
      try {
        const m = await api.request<ResourceMeta>(`${api.prefix}/meta/resources/${encodeURIComponent(name)}`);
        return withPrincipal(m, principal.permissions);
      } catch (e) {
        const fb = fallbackMeta(name);
        if (fb && e instanceof ApiError && (e.isNotFound || e.isNetwork || e.status === 501)) return withPrincipal(fb, principal.permissions);
        if (fb && !(e instanceof ApiError)) return withPrincipal(fb, principal.permissions);
        throw e;
      }
    },
  });
}

export function useMetaIndex() {
  const api = useApi();
  return useQuery<MetaIndex>({
    queryKey: ['meta', '*'],
    staleTime: FOREVER,
    gcTime: FOREVER,
    retry: false,
    queryFn: async () => {
      try {
        return await api.request<MetaIndex>(`${api.prefix}/meta/resources`);
      } catch (e) {
        if (e instanceof ApiError && (e.isNotFound || e.isNetwork || e.status === 501)) {
          return { resources: Object.values(FALLBACK_META) };
        }
        throw e;
      }
    },
  });
}

/** When meta carries no permissions but the principal does, scope the meta to the principal. */
function withPrincipal(m: ResourceMeta, perms: string[] | undefined): ResourceMeta {
  if ((!m.permissions || m.permissions.length === 0) && perms && perms.length > 0) return { ...m, permissions: perms };
  return m;
}

/**
 * Does the API offer this action on this resource for the tenant? A tenant on NovaPSA's own backend
 * has every action. On a connected system (Halo), /v1/meta lists only the actions that system
 * implements, so a widget that calls an action directly asks first instead of rendering a certain
 * 501. `path` is the action's route under the resource (e.g. ':id/summary'). Waits until the backend
 * (and, on a connected system, the meta) is known; a meta error means "assume yes".
 */
export function useActionServed(resource: string, path: string): boolean {
  const { overlay } = useDataSource();
  const m = useMeta(overlay ? resource : undefined);
  if (overlay === undefined) return false;
  if (overlay === null || m.isError) return true;
  if (!m.data) return false;
  const want = `/${resource}/${path}`.replace(/:([A-Za-z_]+)/g, '{$1}');
  return m.data.actions.some((a) => a.path === want);
}
